import { useEffect, useMemo, useState } from "react";
import * as api from "../../api/index.js";
import useAxiosData from "../../hooks/useAxiosData.js";
import React from "react";
import {
    ButtonGroup,
    ToggleButton,
    Container,
    Row as BootstrapRow,
    Col,
    Card,
    Form,
    InputGroup
} from "react-bootstrap";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";

const monthNames = [
    "Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.",
    "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."
];

function LoginAnalyticsGraph({ data }) {
    return (
        <Card className="mt-4 shadow-sm">
            <Card.Body>
                <h2 className="text-center mb-3 h4">Login Analytics Graph</h2>
                <div className="chart-container">
                    <ResponsiveContainer width="100%" height={425}>
                        <LineChart
                            height={425}
                            data={data}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="logins"
                                stroke="#004085" // darker for contrast
                                activeDot={{ r: 8 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card.Body>
        </Card>
    );
}

export default function UserAnalytics() {
    const [users] = useAxiosData(api.fetchUsers, []);
    const [logins, setLogins] = useState([]);
    const [category, setCategory] = useState("month");
    const [sortKey, setSortKey] = useState("username");
    const [sortDirection, setSortDirection] = useState("asc");
    const [searchTerm, setSearchTerm] = useState("");
    const hourFormatter = useMemo(
        () => new Intl.DateTimeFormat("en-US", { timeStyle: "short" }),
        []
    );

    async function changeCategory(newCategory) {
        setCategory(newCategory);
        let { data } = await api.fetchLoginData(newCategory);

        if (newCategory === "hour") {
            data = data.map((l) => ({
                logins: l.logins,
                name: hourFormatter.format(new Date(0, 0, 0, l.hour))
            }));
        } else if (newCategory === "day") {
            data = data.map((l) => ({
                logins: l.logins,
                name: `${l.day} ${monthNames[l.month - 1]}`
            }));
        } else if (newCategory === "month") {
            data = data.map((l) => ({
                logins: l.logins,
                name: `${monthNames[l.month - 1]} ${l.year}`
            }));
        } else if (newCategory === "year") {
            data = data.map((l) => ({
                logins: l.logins,
                name: `${l.year}`
            }));
        }

        setLogins(data);
    }

    useEffect(() => {
        changeCategory(category);
    }, []);

    const sortedUsers = useMemo(() => {
        if (!users) return [];

        const calculateAge = (dob) =>
            Math.floor(
                (Date.now() - Date.parse(dob)) /
                (1000 * 60 * 60 * 24 * 365)
            );

        // Filter by search term
        let filtered = users.filter((user) => {
            const searchLower = searchTerm.toLowerCase();
            const age = calculateAge(user.dateOfBirth).toString();
            return (
                user.username.toLowerCase().includes(searchLower) ||
                user.gender.toLowerCase().includes(searchLower) ||
                age.includes(searchLower) ||
                user.ethnicity.toLowerCase().includes(searchLower) ||
                user.community.toLowerCase().includes(searchLower)
            );
        });

        // Sort filtered results
        return filtered.sort((a, b) => {
            let comparison = 0;
            
            switch (sortKey) {
                case "username":
                    comparison = a.username.localeCompare(b.username);
                    break;
                case "gender":
                    comparison = a.gender.localeCompare(b.gender);
                    break;
                case "age":
                    comparison = calculateAge(a.dateOfBirth) - calculateAge(b.dateOfBirth);
                    break;
                case "ethnicity":
                    comparison = a.ethnicity.localeCompare(b.ethnicity);
                    break;
                case "community":
                    comparison = a.community.localeCompare(b.community);
                    break;
                default:
                    return 0;
            }
            
            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [users, sortKey, sortDirection, searchTerm]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDirection("asc");
        }
    };

    const getSortIcon = (key) => {
        if (sortKey !== key) return <span className="text-muted ms-1">↕</span>;
        return sortDirection === "asc" ? <span className="ms-1">↑</span> : <span className="ms-1">↓</span>;
    };

    return (
        <div>
            {/* Fixed ARIA reference: id matches aria-labelledby below */}
            <h1 id="user-analytics-section" className="h2 text-center mb-4">
                User Demographics
            </h1>

            <section aria-labelledby="user-analytics-section">
                <Container fluid>
                    {/* Category Buttons */}
                    <BootstrapRow className="justify-content-center mt-4">
                        <Col xs={12} md={6} className="text-center">
                            <h2 className="h4">Group By:</h2>
                            <ButtonGroup
                                role="group"
                                aria-label="Group login data by time period"
                            >
                                {["Hour", "Day", "Month", "Year"].map((by) => (
                                    <ToggleButton
                                        key={by}
                                        type="radio"
                                        id={`group-${by.toLowerCase()}`}
                                        aria-label={`Group by ${by}`}
                                        onClick={() =>
                                            changeCategory(by.toLowerCase())
                                        }
                                        variant={
                                            category === by.toLowerCase()
                                                ? "dark"
                                                : "outline-dark"
                                        }
                                        checked={category === by.toLowerCase()}
                                    >
                                        {by}
                                    </ToggleButton>
                                ))}
                            </ButtonGroup>
                        </Col>
                    </BootstrapRow>

                    {/* Login Graph */}
                    <LoginAnalyticsGraph data={logins} />

                    <br></br>
                    
                    {/* Search Bar */}
                    <div className="mb-3">
                        <InputGroup>
                            <InputGroup.Text>🔎</InputGroup.Text>
                            <Form.Control
                                id="userSearch"
                                type="search"
                                placeholder="Search users"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Search users"
                            />
                        </InputGroup>
                    </div>

                    {/* Table with sortable headers */}
                    <>
                        {/* Desktop/Tablet: Table view */}
                        <div className="table-responsive d-none d-md-block">
                            <table className="table table-striped table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th 
                                            scope="col" 
                                            onClick={() => handleSort("username")}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                        >
                                            Username {getSortIcon("username")}
                                        </th>
                                        <th 
                                            scope="col"
                                            onClick={() => handleSort("gender")}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                        >
                                            Gender {getSortIcon("gender")}
                                        </th>
                                        <th 
                                            scope="col"
                                            onClick={() => handleSort("age")}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                        >
                                            Age {getSortIcon("age")}
                                        </th>
                                        <th 
                                            scope="col"
                                            onClick={() => handleSort("ethnicity")}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                        >
                                            Ethnicity {getSortIcon("ethnicity")}
                                        </th>
                                        <th 
                                            scope="col"
                                            onClick={() => handleSort("community")}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                        >
                                            Community {getSortIcon("community")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedUsers.length > 0 ? (
                                        sortedUsers.map((user) => (
                                            <tr key={user._id}>
                                                <td>{user.username}</td>
                                                <td>{user.gender}</td>
                                                <td>
                                                    {Math.floor(
                                                        (Date.now() -
                                                            Date.parse(user.dateOfBirth)) /
                                                        (1000 * 60 * 60 * 24 * 365)
                                                    )}
                                                </td>
                                                <td>{user.ethnicity}</td>
                                                <td>{user.community}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">
                                                No users found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile: Card view */}
                        <div className="d-md-none">
                            {sortedUsers.length > 0 ? (
                                sortedUsers.map((user) => (
                                    <div key={user._id} className="card mb-3 shadow-sm">
                                        <div className="card-body">
                                            <h3 className="card-title mb-3 h5">{user.username}</h3>
                                            
                                            <div className="row g-3">
                                                <div className="col-6">
                                                    <div className="text-muted small">Gender</div>
                                                    <div className="fw-bold">{user.gender}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small">Age</div>
                                                    <div className="fw-bold">
                                                        {Math.floor(
                                                            (Date.now() -
                                                                Date.parse(user.dateOfBirth)) /
                                                            (1000 * 60 * 60 * 24 * 365)
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small">Ethnicity</div>
                                                    <div className="fw-bold">{user.ethnicity}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small">Community</div>
                                                    <div className="fw-bold">{user.community}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center p-4 text-muted">
                                    No users found
                                </div>
                            )}
                        </div>
                    </>

                </Container>
            </section>
        </div>
    );
}
